import { useEffect } from "react";

import { css } from "@compiled/react";

import { LoadingSpinner } from "@/components/global-nav/user-profile-new/src/components/spinner";
import { usePriceFinderContext } from "@/components/wpl/pricing/src/price-finder/price-finder-context.tsx";

import {
    CCPOfferingContent,
    useCCPPriceFinderData,
    useHAMSPriceData
} from "../../../../../contentful";
import {
    COUNTRY_CODE_TO_CURRENCY,
    UserLocale,
    useGeoLocation
} from "../../../../../utils/useGeoCurrency";

import {
    CurrencyISOCode,
    DEFAULT_COUNTRY_ISO_CODE,
    DEFAULT_CURRENCY_ISO_CODE,
    OfferingHamsMap,
    OfferingSKUMap,
    CloudProductKey,
    PriceFinderMessages,
    PricingPlans,
    CloudOfferingKey,
    CloudProductValues,
    DefaultPlanLimits,
    SIGNUP_CTA_URL,
    CONTACT_CTA_URL
} from "./constants";
import { PriceCardsContainer } from "./price-card/card-container";
import { PriceFinderInputsRedesign } from "./price-finder-inputs/price-finder-inputs-redesign";
import { PriceFinderContainer, PriceFinderState } from "./store/store";
import {
    usePriceFinderRequests,
    usePricing
} from "./utils/use-price-finder-data";

export type PriceFinderProps = {
    hamsProductKey?: CloudProductValues | "";

    signupURL?: string;
    halpSignupURLs?: string[];

    countryCode?: UserLocale;
    localeISOCode?: string;
    ccpPricingData: OfferingSKUMap<CloudOfferingKey, CCPOfferingContent>;
    hamsPricingData: OfferingHamsMap<CloudProductKey, PricingPlans>;
    messages: PriceFinderMessages;
};

export const PriceFinder = ({
    hamsProductKey = "",
    signupURL = SIGNUP_CTA_URL,
    ccpPricingData,
    hamsPricingData,
    countryCode = DEFAULT_COUNTRY_ISO_CODE,
    localeISOCode = "en-US",
    messages
}: PriceFinderProps) => {
    const {
        productPlans,
        defaultUnitCount,
        enterpriseMinimumCount = DefaultPlanLimits.UNIT_MIN_ENTERPRISE,
        contactURL = CONTACT_CTA_URL
    } = usePriceFinderContext();

    const currencyISOCode = (COUNTRY_CODE_TO_CURRENCY[countryCode] ||
        DEFAULT_CURRENCY_ISO_CODE) as CurrencyISOCode;

    const initialFetchPriceCalcs = usePriceFinderRequests(
        hamsProductKey,
        productPlans,
        enterpriseMinimumCount,
        defaultUnitCount,
        "MONTHLY"
    );

    const initialPriceCalcs = usePricing(
        ccpPricingData,
        hamsPricingData,
        initialFetchPriceCalcs,
        currencyISOCode,
        localeISOCode
    );

    const initialState: PriceFinderState = {
        unitCount: defaultUnitCount,
        billingCycle: "MONTHLY",
        priceCalcs: initialPriceCalcs,
        currencyISOCode: currencyISOCode,
        messages: messages
    };

    return (
        <PriceFinderContainer {...initialState}>
            <div css={css({ width: "100%" })}>
                <PriceFinderInputsRedesign />
                <PriceCardsContainer
                    productKey={hamsProductKey}
                    signupURL={signupURL}
                    contactURL={contactURL}
                    hamsProductKey={hamsProductKey}
                    hamsPricingData={hamsPricingData}
                    ccpPricingData={ccpPricingData}
                    localeISOCode={localeISOCode}
                />
            </div>
        </PriceFinderContainer>
    );
};

interface PriceFinderWithFetchProps {
    envSource: NodeJS.ProcessEnv | Window;
    hamsProductKey?: CloudProductValues;
    messages: PriceFinderMessages;
    enterpriseMinimumCount?: number;
    contentfulEnvironment?: string;
}

const PriceFinderWithFetch = ({
    envSource,
    hamsProductKey = "",
    messages,
    contentfulEnvironment
}: PriceFinderWithFetchProps) => {
    const { dataProductKey, productPlans } = usePriceFinderContext();

    const offeringSkus = productPlans
        .map((plan) => {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            return plan.offeringSku!;
        })
        .filter(Boolean);

    const { prices, status, fetchData } = useCCPPriceFinderData(
        offeringSkus,
        envSource["PRODUCT_CATALOG_DELIVERY_KEY"] as string,
        envSource["PRODUCT_CATALOG_SPACE"] as string,
        contentfulEnvironment
    );
    useEffect(() => {
        void fetchData();
    }, []);

    const { prices: hamsPrices, status: hamsStatus } = useHAMSPriceData(
        hamsProductKey,
        envSource["PRODUCT_CATALOG_DELIVERY_KEY"] as string,
        envSource["PRODUCT_CATALOG_SPACE"] as string,
        contentfulEnvironment
    );

    const { countryCode, status: locationStatus } = useGeoLocation(
        envSource["PRODUCT_CATALOG_LOCATION_BASE_URL"] as string
    );

    let forceCountryCode = countryCode;

    if (dataProductKey === "bitbucket") {
        forceCountryCode = DEFAULT_COUNTRY_ISO_CODE;
    }

    if (
        locationStatus == "complete" &&
        status == "complete" &&
        hamsStatus == "complete" &&
        prices &&
        hamsPrices &&
        forceCountryCode
    ) {
        return (
            <PriceFinder
                ccpPricingData={prices}
                hamsPricingData={hamsPrices}
                hamsProductKey={hamsProductKey}
                messages={messages}
                countryCode={forceCountryCode}
            />
        );
    }
    return (
        <div
            css={css({
                display: "flex",
                justifyContent: "center",
                height: "900px",
                alignItems: "center"
            })}
        >
            {LoadingSpinner}
        </div>
    );
};

export interface PriceFinderSectionProps {
    hamsProductKey?: CloudProductValues;
    serverSideCCPPricing?: OfferingSKUMap<CloudOfferingKey, CCPOfferingContent>;
    serverSideHAMSPricing?: OfferingHamsMap<CloudProductKey, PricingPlans>;
    countryCode?: UserLocale;
    messages: PriceFinderMessages;
    contentfulEnvironment?: string;
}

const PriceFinderSection = ({
    hamsProductKey,
    serverSideCCPPricing,
    serverSideHAMSPricing,
    countryCode,
    messages,
    contentfulEnvironment
}: PriceFinderSectionProps) => {
    if (serverSideCCPPricing && serverSideHAMSPricing && countryCode) {
        return (
            <PriceFinder
                ccpPricingData={serverSideCCPPricing}
                hamsPricingData={serverSideHAMSPricing}
                hamsProductKey={hamsProductKey}
                countryCode={countryCode}
                messages={messages}
            />
        );
    }
    // server-side request failed
    if (typeof window !== "undefined") {
        // client side render
        return (
            <PriceFinderWithFetch
                hamsProductKey={hamsProductKey}
                messages={messages}
                envSource={window}
                contentfulEnvironment={contentfulEnvironment}
            />
        );
    }
    // server side render
    return (
        <PriceFinderWithFetch
            hamsProductKey={hamsProductKey}
            envSource={process.env}
            messages={messages}
            contentfulEnvironment={contentfulEnvironment}
        />
    );
};

export default PriceFinderSection;
